import { useEffect, useState } from 'react'
import { AlertCircle, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { formatDateTime, formatNaira, formatTimeAgo } from '@/lib/format'
import { cn } from '@/lib/cn'

interface Props {
  balance: {
    available_minor: number
    ledger_minor: number
    as_of: string
  } | null
}

const STALE_MS = 5 * 60_000

/**
 * AF-132 — balance snapshot panel.
 *
 * The "as of" timestamp is rendered prominently per the technical notes
 * ("stale balance is a real investigation hazard"). When the snapshot is
 * older than 5 minutes we surface an amber banner so analysts don't decide
 * on stale numbers.
 */
export function BalanceSnapshot({ balance }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Balance snapshot</CardTitle>
      </CardHeader>
      <CardContent>
        {!balance ? (
          <p className="text-sm text-muted-fg">
            Real-time balance not available. The customer may not have authorised
            balance pulls; ask compliance to verify.
          </p>
        ) : (
          <Snapshot balance={balance} />
        )}
      </CardContent>
    </Card>
  )
}

function Snapshot({
  balance,
}: {
  balance: NonNullable<Props['balance']>
}) {
  // `Date.now()` is impure in render — read it once on mount and let the
  // banner re-evaluate every minute via setInterval. Lint rule
  // `react-hooks/purity` enforces this.
  const [now, setNow] = useState<number>(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])
  const ageMs = now - new Date(balance.as_of).getTime()
  const stale = ageMs > STALE_MS
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid grid-cols-2 gap-3">
        <Stat label="Available" value={formatNaira(balance.available_minor)} highlight />
        <Stat label="Ledger" value={formatNaira(balance.ledger_minor)} />
      </dl>
      <p
        className={cn(
          'flex items-center gap-2 rounded-md border px-3 py-2 text-xs',
          stale
            ? 'border-status-pending/30 bg-status-pending/5 text-status-pending'
            : 'border-border bg-card text-muted-fg',
        )}
      >
        {stale ? (
          <>
            <AlertCircle size={12} aria-hidden /> Snapshot is{' '}
            <span className="font-medium">{formatTimeAgo(balance.as_of)}</span> —
            request a fresh balance before acting on this number.
          </>
        ) : (
          <>
            <Clock size={12} aria-hidden /> Balance as of{' '}
            <span className="font-medium">{formatDateTime(balance.as_of)}</span>{' '}
            ({formatTimeAgo(balance.as_of)})
          </>
        )}
      </p>
    </div>
  )
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <div className="rounded border border-border bg-card p-3 text-center">
      <dt className="text-[0.625rem] uppercase tracking-wide text-muted-fg">
        {label}
      </dt>
      <dd
        className={cn(
          'mt-1 font-mono tabular-nums',
          highlight ? 'text-xl font-semibold' : 'text-base',
        )}
      >
        {value}
      </dd>
    </div>
  )
}
